import useLatest from "@-/com.npmjs.use-latest";
import {
  type LearnCardDestinationOrigin,
  type LearnCardDestinationResolution,
  resolveLearnCardDestination,
} from "@-/dev.improve.haniverse.lib.learn.destination";
import { useCallback } from "react";

import { useLearnCardArrival } from "#use-learn-card-arrival";
import { useLearnCardDestinationReads } from "#use-learn-card-destination-reads";
import { useLearnCardDestinationSettings } from "#use-learn-card-destination-settings";

/**
 * `resolveLearnCardDestination` bound to the app's reads and the current settings. The returned
 * function is stable; settings are read at CALL time so a decision always reflects the session
 * as it is when it is made, not when the component last rendered.
 *
 * `predictionOrigins` plans ahead (the deck's flashcard queue): the origin and the cards before it
 * count as done although their writes may not have landed. `committed` is a route-driven card
 * leaving for the resolution right away, so arriving there takes effect (`useLearnCardArrival`).
 */
export function useResolveLearnCardDestination(): (
  origin: LearnCardDestinationOrigin,
  options?: { committed?: boolean; predictionOrigins?: readonly LearnCardDestinationOrigin[] },
) => Promise<LearnCardDestinationResolution> {
  const reads = useLearnCardDestinationReads();
  const settingsRef = useLatest(useLearnCardDestinationSettings());
  const arrive = useLearnCardArrival();

  return useCallback(
    async (
      origin: LearnCardDestinationOrigin,
      options?: { committed?: boolean; predictionOrigins?: readonly LearnCardDestinationOrigin[] },
    ) => {
      const resolution = await resolveLearnCardDestination({
        origin,
        predictionOrigins: options?.committed ? undefined : options?.predictionOrigins,
        reads,
        settings: settingsRef.current,
      });
      if (options?.committed) {
        arrive(resolution.destination.kind);
      }
      return resolution;
    },
    [arrive, reads, settingsRef],
  );
}
